/**
 * Fullscreen image viewer with wheel-zoom (cursor-anchored), drag-to-pan,
 * double-click to toggle fit/100%, ←/→ to navigate, ESC/backdrop to close.
 */

export interface PhotoLightboxOptions {
  photos: string[];
  startIdx: number;
  /** Resolves a filename to the full-resolution URL. */
  urlFor: (filename: string) => string;
}

export function openPhotoLightbox(opts: PhotoLightboxOptions): void {
  if (opts.photos.length === 0) return;

  let idx = Math.max(0, Math.min(opts.startIdx, opts.photos.length - 1));

  const backdrop = document.createElement("div");
  backdrop.style.cssText = `
    position: fixed; inset: 0; z-index: 300;
    background: rgba(0,0,0,.92);
    display: flex; align-items: center; justify-content: center;
    overflow: hidden; user-select: none; touch-action: none;
  `;

  const stage = document.createElement("div");
  stage.style.cssText = `
    position: absolute; inset: 0;
    display: flex; align-items: center; justify-content: center;
    cursor: grab;
  `;

  const img = document.createElement("img");
  img.draggable = false;
  img.style.cssText = `
    max-width: 100%; max-height: 100%; object-fit: contain;
    transform-origin: 0 0;
    will-change: transform;
    pointer-events: none;
  `;
  stage.appendChild(img);
  backdrop.appendChild(stage);

  // ── HUD ──────────────────────────────────────────────────────────────────
  const hud = document.createElement("div");
  hud.style.cssText = `
    position: absolute; top: 12px; left: 0; right: 0;
    display: flex; justify-content: center; gap: 10px;
    color: #ddd; font: 13px/1 system-ui, sans-serif;
    pointer-events: none;
  `;
  const counter = document.createElement("span");
  counter.style.cssText = "background: rgba(0,0,0,.5); padding: 6px 10px; border-radius: 12px;";
  hud.appendChild(counter);
  backdrop.appendChild(hud);

  const closeBtn = document.createElement("button");
  closeBtn.textContent = "✕";
  closeBtn.style.cssText = `
    position: absolute; top: 12px; right: 14px;
    width: 36px; height: 36px; border-radius: 50%;
    background: rgba(0,0,0,.5); color: #fff; border: none;
    font-size: 18px; cursor: pointer;
  `;
  closeBtn.onclick = (e) => { e.stopPropagation(); close(); };
  backdrop.appendChild(closeBtn);

  const mkArrow = (dir: "prev" | "next") => {
    const b = document.createElement("button");
    b.textContent = dir === "prev" ? "‹" : "›";
    b.style.cssText = `
      position: absolute; top: 50%; transform: translateY(-50%);
      ${dir === "prev" ? "left: 14px" : "right: 14px"};
      width: 44px; height: 60px; border-radius: 8px;
      background: rgba(0,0,0,.45); color: #fff; border: none;
      font-size: 30px; line-height: 1; cursor: pointer;
    `;
    b.onclick = (e) => { e.stopPropagation(); navigate(dir === "prev" ? -1 : 1); };
    return b;
  };
  const prevBtn = mkArrow("prev");
  const nextBtn = mkArrow("next");
  backdrop.appendChild(prevBtn);
  backdrop.appendChild(nextBtn);

  // ── Zoom/pan state ───────────────────────────────────────────────────────
  // (sx, sy) = top-left of image (in viewport pixels), scale = current scale
  // relative to the natural "fit" rendering of <img>.
  let scale = 1;
  let sx = 0;
  let sy = 0;
  let baseW = 0;
  let baseH = 0;

  const applyTransform = () => {
    img.style.transform = `translate(${sx}px, ${sy}px) scale(${scale})`;
  };

  const fitImage = () => {
    // Use rendered size of <img> as the natural "fit" baseline.
    const rect = img.getBoundingClientRect();
    baseW = rect.width;
    baseH = rect.height;
    scale = 1;
    const stageRect = stage.getBoundingClientRect();
    sx = (stageRect.width - baseW) / 2;
    sy = (stageRect.height - baseH) / 2;
    applyTransform();
  };

  // ── Load image ───────────────────────────────────────────────────────────
  const load = () => {
    counter.textContent = opts.photos.length > 1
      ? `${idx + 1} / ${opts.photos.length}`
      : "";
    prevBtn.style.display = opts.photos.length > 1 ? "" : "none";
    nextBtn.style.display = opts.photos.length > 1 ? "" : "none";
    img.style.opacity = "0";
    img.onload = () => {
      img.style.opacity = "1";
      // Wait a tick so the browser lays out before we measure.
      requestAnimationFrame(fitImage);
    };
    img.src = opts.urlFor(opts.photos[idx]);
  };

  const navigate = (dir: number) => {
    idx = (idx + dir + opts.photos.length) % opts.photos.length;
    load();
  };

  // ── Wheel-zoom anchored at cursor ────────────────────────────────────────
  stage.addEventListener("wheel", (e) => {
    e.preventDefault();
    const rect = stage.getBoundingClientRect();
    const cx = e.clientX - rect.left;
    const cy = e.clientY - rect.top;
    const factor = Math.exp(-e.deltaY * 0.0015);
    const next = Math.max(0.2, Math.min(10, scale * factor));
    const k = next / scale;
    // Keep the point under the cursor stationary:
    // new_s = c - k * (c - s)
    sx = cx - k * (cx - sx);
    sy = cy - k * (cy - sy);
    scale = next;
    applyTransform();
  }, { passive: false });

  // ── Drag-to-pan ──────────────────────────────────────────────────────────
  let dragging = false;
  let dragStartX = 0;
  let dragStartY = 0;
  let dragOrigSx = 0;
  let dragOrigSy = 0;

  stage.addEventListener("pointerdown", (e) => {
    if (e.button !== 0) return;
    dragging = true;
    dragStartX = e.clientX;
    dragStartY = e.clientY;
    dragOrigSx = sx;
    dragOrigSy = sy;
    stage.style.cursor = "grabbing";
    stage.setPointerCapture(e.pointerId);
  });
  stage.addEventListener("pointermove", (e) => {
    if (!dragging) return;
    sx = dragOrigSx + (e.clientX - dragStartX);
    sy = dragOrigSy + (e.clientY - dragStartY);
    applyTransform();
  });
  const endDrag = (e: PointerEvent) => {
    if (!dragging) return;
    dragging = false;
    stage.style.cursor = "grab";
    try { stage.releasePointerCapture(e.pointerId); } catch { /* noop */ }
    // Treat as click if we barely moved — close on backdrop click.
    const dx = e.clientX - dragStartX;
    const dy = e.clientY - dragStartY;
    if (dx * dx + dy * dy < 9 && e.target === stage) {
      close();
    }
  };
  stage.addEventListener("pointerup", endDrag);
  stage.addEventListener("pointercancel", endDrag);

  // ── Double-click fit ↔ 1:1 (natural pixels) ──────────────────────────────
  stage.addEventListener("dblclick", (e) => {
    const rect = stage.getBoundingClientRect();
    const cx = e.clientX - rect.left;
    const cy = e.clientY - rect.top;
    const naturalScale = img.naturalWidth && baseW ? img.naturalWidth / baseW : 1;
    const target = Math.abs(scale - 1) < 0.01 ? naturalScale : 1;
    const k = target / scale;
    sx = cx - k * (cx - sx);
    sy = cy - k * (cy - sy);
    scale = target;
    applyTransform();
  });

  // ── Keyboard ─────────────────────────────────────────────────────────────
  const onKey = (e: KeyboardEvent) => {
    if (e.key === "Escape") close();
    else if (e.key === "ArrowLeft" && opts.photos.length > 1) navigate(-1);
    else if (e.key === "ArrowRight" && opts.photos.length > 1) navigate(1);
  };
  document.addEventListener("keydown", onKey);

  const onResize = () => fitImage();
  window.addEventListener("resize", onResize);

  function close(): void {
    document.removeEventListener("keydown", onKey);
    window.removeEventListener("resize", onResize);
    backdrop.remove();
  }

  document.body.appendChild(backdrop);
  load();
}
